import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { User, AlertTriangle, CheckCircle, Phone, Building, Share2, ChevronDown, ChevronUp, Loader2, Truck } from 'lucide-react';
import { safeFetch } from '../utils/safeFetch';
import { API_URL } from '../config';
import DepartmentSelector from '../components/DepartmentSelector';
import Dropdown from '../components/Dropdown';
import useToast from '../hooks/useToast';
import { generateLogin } from '../utils/loginGenerator';
import { haptic } from '../utils/vibration';
import { USER_STAGE_ORDER, STAGES_CONFIG, STAGE_BADGE_STYLES } from '../utils/stages';
import { useDryCleaningEnabled } from '../utils/featureFlags';

// Функция валидации телефона
const validatePhone = (phone) => {
  const cleanPhone = phone.replace(/\D/g, '');
  return cleanPhone.length === 11 && cleanPhone.startsWith('79');
};

const isFullPhoneNumber = (phone) => {
  const cleanPhone = phone.replace(/\D/g, '');
  return cleanPhone.length === 11 && cleanPhone.startsWith('79');
};

const getPhoneError = (phone) => {
  const cleanPhone = phone.replace(/\D/g, '');
  if (!cleanPhone || cleanPhone === '7') return '';
  if (cleanPhone.length >= 2 && !cleanPhone.startsWith('79')) {
    return 'Номер должен начинаться с +79';
  }
  if (cleanPhone.length === 11 && !isFullPhoneNumber(phone)) {
    return 'Номер должен начинаться с +79 и содержать 11 цифр';
  }
  return '';
};

// Функция форматирования телефона
const formatPhone = (value) => {
  let cleaned = value.replace(/\D/g, '');
  
  // Автоматически добавляем +7 если ввод начинается с 8 или пустой
  if (cleaned.startsWith('8')) {
    cleaned = '7' + cleaned.substring(1);
  } else if (!cleaned.startsWith('7') && cleaned.length > 0) {
    cleaned = '7' + cleaned;
  }
  
  // Ограничиваем длину
  if (cleaned.length > 11) {
    cleaned = cleaned.substring(0, 11);
  }
  
  // Форматируем как +7 (999) 123-45-67
  let formatted = '+7';
  if (cleaned.length > 1) {
    formatted += ' (' + cleaned.substring(1, 4);
    if (cleaned.length > 4) {
      formatted += ') ' + cleaned.substring(4, 7);
      if (cleaned.length > 7) {
        formatted += '-' + cleaned.substring(7, 9);
        if (cleaned.length > 9) {
          formatted += '-' + cleaned.substring(9, 11);
        }
      }
    }
  }
  
  return formatted;
};

const CreateUser = ({ onBack, onUserCreated }) => {
  const isDryCleaningEnabled = useDryCleaningEnabled();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    phone: '+7',
    userType: 'user'
  });
  
  // Add departments state
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [showDepartments, setShowDepartments] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showRoles, setShowRoles] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdLogin, setCreatedLogin] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const { showToast } = useToast();
  const inputClass = (field) => `w-full min-h-[44px] px-3 py-2.5 border rounded-xl focus:outline-none focus:ring-2 transition-colors bg-white dark:bg-gray-900 dark:text-gray-100 text-sm ${
    errors[field]
      ? 'border-red-300 focus:ring-red-500/40 dark:border-red-500'
      : 'border-gray-200 focus:ring-blue-500/40 focus:border-blue-500 dark:border-gray-700'
  }`;
  const closeCreateUser = () => {
    haptic('close');
    window.dispatchEvent(new CustomEvent('createuserback'));
    sessionStorage.removeItem('showCreateUser');
    onBack && onBack();
  };
  const toggleRole = (roleName) => {
    haptic('selection');
    setSelectedRoles(prev => (
      prev.includes(roleName)
        ? prev.filter(role => role !== roleName)
        : [...prev, roleName]
    ));
  };
  const orderStages = Object.fromEntries(
    Object.entries(STAGES_CONFIG).map(([key, config]) => [
      key,
      {
        name: config.label,
        icon: config.icon,
        color: STAGE_BADGE_STYLES[config.color] || STAGE_BADGE_STYLES.blue
      }
    ])
  );


  // Прокручиваем страницу вверх при показе страницы успеха
  useEffect(() => {
    if (success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [success]);

  // Получаем доступные типы пользователей в зависимости от текущего пользователя
  const getCurrentUserType = () => localStorage.getItem('user_type');
  
  const getAvailableUserTypes = () => {
    const currentUserType = getCurrentUserType();
    const allTypes = [
      { value: 'user', label: 'Пользователь', description: 'Базовые права доступа' },
      { value: 'dispatcher', label: 'Диспетчер', description: 'Управление заказами' },
      { value: 'admin', label: 'Администратор', description: 'Полные права управления' },
      { value: 'developer', label: 'Разработчик', description: 'Системные права' }
    ];
    
    // Админы не могут создавать разработчиков
    if (currentUserType === 'admin') {
      return allTypes.filter(type => type.value !== 'developer');
    }
    
    return allTypes;
  };

  // Функция генерации пароля
  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
    if (cryptoApi?.getRandomValues) {
      const bytes = new Uint32Array(length);
      cryptoApi.getRandomValues(bytes);
      return Array.from(bytes, (value) => charset.charAt(value % charset.length)).join('');
    }
    let fallbackPassword = "";
    for (let i = 0, n = charset.length; i < length; ++i) {
      fallbackPassword += charset.charAt(Math.floor(Math.random() * n));
    }
    return fallbackPassword;
  };

  const validateForm = async () => {
    const newErrors = {};

    // Проверка имени
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Имя обязательно';
    }

    // Проверка фамилии
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Фамилия обязательна';
    }

    // Проверка телефона (теперь обязательно)
    if (!formData.phone || formData.phone === '+7') {
      newErrors.phone = 'Телефон обязателен';
    } else if (getPhoneError(formData.phone) || !validatePhone(formData.phone)) {
      newErrors.phone = getPhoneError(formData.phone) || 'Номер должен начинаться с +79 и содержать 11 цифр';
    } else {
      // Проверка уникальности телефона
      try {
        const token = localStorage.getItem('token');
        const response = await safeFetch(`${API_URL}/api/users`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const users = response.users || [];
        const phoneExists = users.some(user => 
          user.phone && user.phone.replace(/\D/g, '') === formData.phone.replace(/\D/g, '')
        );
        
        if (phoneExists) {
          newErrors.phone = 'Пользователь с таким номером телефона уже существует';
        }
      } catch (error) {
        console.error('Ошибка проверки уникальности телефона:', error);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    haptic('tap');
    
    if (!await validateForm()) {
      haptic('error');
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const token = localStorage.getItem('token');
      const currentUserType = localStorage.getItem('user_type');

      // Проверка прав на создание пользователя
      if (currentUserType !== 'admin' && currentUserType !== 'developer') {
        showToast('У вас нет прав для создания пользователей', 'error');
        setLoading(false);
        return;
      }

      // Генерируем базовый логин
      const baseLogin = generateLogin(formData.firstName, formData.lastName);
      if (!baseLogin) {
        showToast('Не удалось сгенерировать логин', 'error');
        setLoading(false);
        return;
      }

      // Проверяем уникальность логина и генерируем финальный
      let finalLogin = baseLogin;
      let counter = 2; // Начинаем с 2, так как первый логин без цифры
      let isLoginUnique = false;

      try {
        const data = await safeFetch(`${API_URL}/api/users`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const existingLogins = new Set(
          (data.users || [])
            .map(user => user.login)
            .filter(Boolean)
            .map(login => login.toLowerCase())
        );

        while (!isLoginUnique && counter <= 100) {
          if (!existingLogins.has(finalLogin.toLowerCase())) {
            isLoginUnique = true;
          } else {
            finalLogin = baseLogin + counter;
            counter++;
          }
        }
      } catch (error) {
        console.error('Ошибка проверки логина:', error);
      }

      if (!isLoginUnique) {
        showToast('Не удалось сгенерировать уникальный логин', 'error');
        setLoading(false);
        return;
      }

      // Генерируем пароль
      const newPassword = generatePassword();

      const userData = {
        login: finalLogin,
        password: newPassword,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim() || null,
        middle_name: formData.middleName.trim() || null,
        phone: formData.phone && formData.phone !== '+7' ? formData.phone : null,
        user_type: formData.userType,
        user_status: 'active',
        user_roles: JSON.stringify(selectedRoles),
        departments: JSON.stringify(selectedDepartments) // Это уже правильно
      };

      await safeFetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      setCreatedLogin(finalLogin);
      setGeneratedPassword(newPassword);
      setSuccess(true);
      haptic('success');

    } catch (error) {
      console.error('Ошибка создания пользователя:', error);
      showToast(error.message || 'Ошибка создания пользователя', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    // Особая обработка для телефона
    if (name === 'phone') {
      processedValue = formatPhone(value);
      const phoneError = getPhoneError(processedValue);
      setErrors(prev => ({
        ...prev,
        phone: phoneError
      }));
      if (isFullPhoneNumber(processedValue)) {
        requestAnimationFrame(() => e.target.blur());
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Очищаем ошибку при изменении поля
    if (name !== 'phone' && errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleShare = () => {
    haptic('tap');
    const textToCopy = `Логин: ${createdLogin}\nПароль: ${generatedPassword}\n\nПожалуйста, измените пароль при первом входе в систему.`;

    if (navigator.share) {
      navigator.share({
        title: 'Учетные данные нового сотрудника',
        text: textToCopy
      }).catch(error => console.log('Ошибка при попытке поделиться:', error));
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          showToast('Учетные данные скопированы в буфер обмена!');
        })
        .catch(err => {
          console.error('Ошибка копирования:', err);
        });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        showToast('Учетные данные скопированы в буфер обмена!');
      } catch (err) {
        showToast('Скопируйте данные вручную', 'info');
      }
      document.body.removeChild(textarea);
    }
  };

  if (success) {
    return (
      <>
        

        <motion.div
          className="space-y-4"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-sm">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-green-600 dark:text-green-400">Готово</p>
                <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Пользователь создан
                </h1>
              </div>
            </div>
          
            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 mb-4 dark:border-blue-900/40 dark:bg-blue-900/20">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Учетные данные нового сотрудника:
              </p>
              <div className="grid grid-cols-1 gap-2">
                <div className="rounded-xl bg-white px-3 py-2 dark:bg-gray-900">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Логин</p>
                  <p className="font-mono font-bold text-blue-600 dark:text-blue-400 break-all">
                    {createdLogin}
                  </p>
                </div>
                <div className="rounded-xl bg-white px-3 py-2 dark:bg-gray-900">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Пароль</p>
                  <p className="font-mono font-bold text-blue-600 dark:text-blue-400 break-all">
                    {generatedPassword}
                  </p>
                </div>
              </div>
            </div>
          
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 mb-4 border border-amber-100 dark:border-amber-900/40">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Обязательно сообщите сотруднику учетные данные и напомните о необходимости смены пароля при первом входе в систему.
                </p>
              </div>
            </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={handleShare}
              className="flex-1 min-h-[44px] flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:scale-[0.99] transition-all font-semibold"
            >
              <Share2 className="w-5 h-5" />
              <span>Поделиться</span>
            </button>
            <button
              onClick={closeCreateUser}
              className="flex-1 min-h-[44px] px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 active:scale-[0.99] transition-all font-semibold"
            >
              Закрыть
            </button>
          </div>
        </div>
      </motion.div>

      
    </>
    );
  }

  return (
    <>
      

      <motion.div
        className="space-y-4"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
      >
        {/* Форма создания пользователя */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Имя, Фамилия, Отчество */}
          <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-3 dark:border-gray-800 dark:bg-gray-950/30">
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                <User className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Основное</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">ФИО, телефон и тип доступа</p>
              </div>
            </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                Имя <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={inputClass('firstName')}
                placeholder="Имя"
                disabled={loading}
              />
              {errors.firstName && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                Фамилия <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={inputClass('lastName')}
                placeholder="Фамилия"
                disabled={loading}
              />
              {errors.lastName && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.lastName}</p>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                Отчество
              </label>
              <input
                type="text"
                name="middleName"
                value={formData.middleName}
                onChange={handleInputChange}
                className={inputClass('middleName')}
                placeholder="Отчество"
                disabled={loading}
              />
              {errors.middleName && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.middleName}</p>
              )}
            </div>
          </div>

          {/* Телефон */}
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
              Телефон <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                inputMode="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`${inputClass('phone')} pr-10`}
                placeholder="+7 (999) 123-45-67"
                disabled={loading}
              />
              <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            {errors.phone && (
              <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Тип пользователя */}
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
              Тип пользователя <span className="text-red-500">*</span>
            </label>
            <Dropdown
              value={formData.userType}
              onChange={(value) => {
                haptic('selection');
                setFormData(prev => ({ ...prev, userType: value }));
              }}
              options={getAvailableUserTypes().map(type => ({
                value: type.value,
                label: `${type.label} - ${type.description}`
              }))}
              placeholder="Выберите тип пользователя"
              disabled={loading}
            />
          </div>
          </div>

          {/* Доступные этапы (спойлер) */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => {
                haptic(showRoles ? 'close' : 'open');
                setShowRoles(!showRoles);
              }}
              className="flex min-h-[48px] items-center justify-between w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2.5 text-left shadow-sm transition-all active:scale-[0.99]"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300">
                  <Truck className="w-4 h-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">Доступные этапы</span>
                  <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{selectedRoles.length || 0} выбрано</span>
                </span>
              </span>
              {showRoles ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            
            <AnimatePresence initial={false}>
              {showRoles && (
              <motion.div
                className="grid grid-cols-1 gap-2 overflow-hidden sm:grid-cols-2"
                initial={{ opacity: 0, height: 0, y: -4 }}
                animate={{ opacity: 1, height: 'auto', y: 0 }}
                exit={{ opacity: 0, height: 0, y: -4 }}
                transition={{ duration: 0.18 }}
              >
                {USER_STAGE_ORDER.filter(key => isDryCleaningEnabled || key !== 'cleaning').map(key => {
                  const stage = orderStages[key];
                  if (!stage) return null;
                  const Icon = stage.icon;
                  const isSelected = selectedRoles.includes(stage.name);

                  return (
                    <label
                      key={key}
                      className={`flex min-h-[46px] items-center gap-2 rounded-xl border p-2.5 cursor-pointer transition-all active:scale-[0.98] ${
                        isSelected
                          ? 'border-blue-300 bg-blue-50 shadow-sm dark:border-blue-800 dark:bg-blue-900/20'
                          : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRole(stage.name)}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className={`text-xs ${isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-600 dark:text-gray-400'}`}>
                        {stage.name}
                      </span>
                    </label>
                  );
                })}
              </motion.div>
            )}
            </AnimatePresence>
          </div>

          {/* Подразделения (спойлер) */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => {
                haptic(showDepartments ? 'close' : 'open');
                setShowDepartments(!showDepartments);
              }}
              className="flex min-h-[48px] items-center justify-between w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2.5 text-left shadow-sm transition-all active:scale-[0.99]"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300">
                  <Building className="w-4 h-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">Подразделения</span>
                  <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{selectedDepartments.length || 0} выбрано</span>
                </span>
              </span>
              {showDepartments ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            
            <AnimatePresence initial={false}>
              {showDepartments && (
              <motion.div
                className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50/60 p-2 dark:border-gray-800 dark:bg-gray-950/30"
                initial={{ opacity: 0, height: 0, y: -4 }}
                animate={{ opacity: 1, height: 'auto', y: 0 }}
                exit={{ opacity: 0, height: 0, y: -4 }}
                transition={{ duration: 0.18 }}
              >
                <DepartmentSelector
                  selectedDepartments={selectedDepartments}
                  onChange={setSelectedDepartments}
                  disabled={loading}
                />
              </motion.div>
            )}
            </AnimatePresence>
          </div>

          {/* Кнопки */}
          <div className="-mx-4 -mb-4 flex flex-col-reverse gap-2 border-t border-gray-100 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900 sm:mx-0 sm:mb-0 sm:flex-row sm:border-t sm:bg-transparent sm:px-0 sm:pb-0">
            <button
              type="button"
              onClick={closeCreateUser}
              className="w-full sm:flex-1 min-h-[44px] px-6 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-[0.99] transition-all font-semibold"
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:flex-1 min-h-[44px] px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99] transition-all font-semibold flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Создание...</span>
                </>
              ) : (
                'Создать пользователя'
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>

      
    </>
  );
};

export default CreateUser;
